import { VIZ_CATEGORICAL } from '@splunk/visualization-color-palettes';
import { LAYOUT_TYPE } from '../utils/constants';
import { COLOR_OR_TOKEN_PATTERN_WITH_RGBA, getPattern } from '../utils';
import { enhanceOptionsSchemaWithDynamicOptions } from '../utils/enhanceSchemaWithDynamicOptions';

export const areaOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    acceleratedRender: {
        default: false,
        description:
            'Specify whether to enable canvas rendering to render large datasets more efficiently. This mode improves initial render time and interaction responsiveness for large time-series datasets.',
        type: 'boolean',
    },
    x: {
        default: '> primary | seriesByIndex(0)',
        description: 'Specify the data source to apply to the x-axis.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    y: {
        default: '> primary | frameBySeriesIndexRange(1)',
        description: 'Specify the data source to apply to the y-axis.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    y2: {
        description: 'Specify the data source to apply to the second y-axis',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    xField: {
        default: '> x | getField()',
        description: 'Specify the field to map to the x-axis.',
        type: 'string',
    },
    yFields: {
        default: '> y | getField()',
        description: 'Specify the field to map to the y-axis.',
        type: 'string',
    },
    additionalTooltipFields: {
        default: [],
        description:
            'Specify the fields to add to the default set of tooltips. Tooltips appear when you hover over events. These fields and their corresponding values are shown in addition to the ones displayed by default.',
        type: 'array',
        items: { type: 'string' },
    },
    annotationColor: {
        description: `Specify the annotation colors. For example, [“#FF0000”, “#0000FF", “#008000”]. You can use a data source or a hexadecimal code to apply the color.`,
        type: 'array',
        items: { type: 'string' },
    },
    annotationLabel: {
        description: `Specify a list of labels to use for annotations. For example, [“Sales”, “Revenue”, “Orders”]. You might use a data source to apply the labels.`,
        type: 'array',
        items: { type: 'string' },
    },
    annotationX: {
        description: 'Specify a data source field to apply to the event annotation on the x-axis.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    areaOpacity: {
        description:
            'Specify the opacity of the area chart. Values can range from 0 (transparent) to 1 (opaque). The fill opacity is calculated by applying areaOpacity squared.',
        type: 'number',
        default: 0.75,
    },
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color to use for the background. You can use a data source or hexadecimal code to apply the color.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    dataValuesDisplay: {
        description:
            'Specify the labels to display. Enter "all" to show labels for all data points, "off" to show no labels, or "minmax" to show high and low values.',
        type: 'string',
        pattern: getPattern(['off', 'all', 'minmax']),
        default: 'off',
    },
    legendDisplay: {
        description:
            'Specify the location of the legend on the panel. By default, legendDisplay is off when splitByLayout is trellis, and right otherwise. You must select the Standard legend mode to make this change.',
        pattern: getPattern(['right', 'left', 'top', 'bottom', 'off']),
        type: 'string',
    },
    legendLabels: {
        description: `Specify a list of labels to populate the legend in advance. For example, ["percent", "count"].`,
        type: 'array',
        items: { type: 'string' },
    },
    legendMode: {
        default: 'standard',
        description:
            'Specify visual and behavioral settings for the tooltip and legend. "seriesCompare" is useful when comparing series.',
        pattern: getPattern(['standard', 'seriesCompare']),
        type: 'string',
    },
    legendTruncation: {
        default: 'ellipsisEnd',
        description:
            'Specify how to display legend labels when they overflow the layout boundaries by replacing overflow text with an ellipsis.',
        pattern: getPattern(['ellipsisEnd', 'ellipsisMiddle', 'ellipsisStart', 'ellipsisOff']),
        type: 'string',
    },
    lineDashStyle: {
        default: 'solid',
        description: 'Specify a dash style for all overlay field lines.',
        pattern: getPattern([
            'solid',
            'shortDash',
            'shortDot',
            'shortDashDot',
            'shortDashDotDot',
            'dot',
            'dash',
            'longDash',
            'dashDot',
            'longDashDot',
            'longDashDotDot',
        ]),
        type: 'string',
    },
    lineDashStylesByField: {
        description: `Specify a dash style to use for overlay lines for each field. For example, {"count": "longDashDot", "percent": "longDashDotDot"}.
        Valid values: "solid", "shortDash", "shortDot", "shortDashDot", "shortDashDotDot", "dot", "dash",
        "longDash", "dashDot", "longDashDot", "longDashDotDot".`,
        type: 'object',
    },
    stepPosition: {
        description:
            'Specify stepped interpolation position for area series in accelerated render mode. Valid values: "off", "before", "after".',
        pattern: getPattern(['off', 'before', 'after']),
        type: 'string',
        default: 'off',
    },
    stepPositionsByField: {
        description: `Specify stepped interpolation position for each field in accelerated render mode. For example, {"count": "before", "percent": "off"}.
        Valid values: "off", "before", "after".`,
        type: 'object',
    },
    lineWidth: {
        default: 2,
        description: 'Specify the line width (px) for overlay field lines.',
        type: 'number',
    },
    nullValueDisplay: {
        default: 'gaps',
        description: 'Specify the handling of null values for area and line charts .',
        pattern: getPattern(['gaps', 'zero', 'connect']),
        type: 'string',
    },
    resultLimit: {
        default: 50000,
        description: 'Specify the number of data points to render in a chart.',
        type: 'number',
    },
    seriesColors: {
        default: VIZ_CATEGORICAL,
        description: `Specify the colors to use in a series. For example, ["#FF0000", "#0000FF", "#008000"].`,
        type: 'array',
        items: { type: 'string' },
    },
    seriesColorsByField: {
        description: `Specify the colors to use for specific fields in a series. For example, {“count”: “#008000”, “percent”: “#FFA500”}.`,
        type: 'object',
    },
    splitByLayout: {
        default: 'off',
        description:
            'Specify the layout method by which to display the visualization, which splits the data into individual visualizations based on a certain category.',
        type: 'string',
        pattern: getPattern([LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis]),
        enum: [LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis],
    },
    stackMode: {
        description: 'Specify stack mode.',
        type: 'string',
        pattern: getPattern(['auto', 'stacked', 'stacked100']),
        default: 'auto',
    },
    showIndependentYRanges: {
        description: 'Specify whether split series charts have independent y-ranges.',
        type: 'boolean',
        default: false,
    },
    showSplitSeries: {
        description:
            'Specify whether to split a multi-series chart into separate charts that are stacked from top to bottom; use one chart for each series.',
        type: 'boolean',
        default: false,
    },
    showLineSmoothing: {
        default: false,
        description:
            'Specify whether lines are straight or smoothed using curves. Only supported in accelerated render mode.',
        type: 'boolean',
    },
    showLines: {
        description: 'Specify whether to show lines in area charts.',
        default: true,
        type: 'boolean',
    },
    showXMajorGridLines: {
        description: 'Specify whether to show major grid lines on the x-axis.',
        type: 'boolean',
        default: false,
    },
    showYMajorGridLines: {
        description: 'Specify whether to show major grid lines on the y-axis.',
        type: 'boolean',
        default: true,
    },
    showY2MajorGridLines: {
        description: 'Specify whether to show major grid lines on the second y-axis.',
        type: 'boolean',
        default: false,
    },
    showYMinorGridLines: {
        description: 'Specify whether to show minor grid lines on the y-axis.',
        type: 'boolean',
        default: false,
    },
    showY2MinorGridLines: {
        description: 'Specify whether to show minor grid lines on the second y-axis.',
        type: 'boolean',
        default: false,
    },
    showYAxisExtendedRange: {
        description: 'Specify whether to extend the y-axis to include whole major tick marks.',
        type: 'boolean',
        default: true,
    },
    showYAxisWithZero: {
        description: 'Specify whether to include zero in the y-axis range.',
        type: 'boolean',
        default: false,
    },
    showY2AxisWithZero: {
        description: 'Specify whether to include zero in the second y-axis range.',
        type: 'boolean',
        default: false,
    },
    trellisBackgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color used for the trellis container background by using a hexadecimal code. For example, #0000FF.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    trellisColumns: {
        description:
            'Specify the number of visualizations to display in a given row of the trellis container. The remaining visualizations will wrap accordingly. If nothing is specified, it will be auto-set based on the trellisMinColumnWidth. The minimum value is 1.',
        type: 'number',
        minimum: 1,
        default: 3,
    },
    trellisMinColumnWidth: {
        default: 250,
        minimum: 1,
        description:
            'Specify the minimum width, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisPageCount: {
        default: 20,
        minimum: 1,
        description:
            'Specify the maximum number of visualizations to display in a single page in the trellis container. The remaining visualizations will paginate accordingly. The minimum value is 1.',
        type: 'number',
    },
    trellisRowHeight: {
        default: 180,
        minimum: 1,
        description:
            'Specify the height, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisSharedScale: {
        default: true,
        type: 'boolean',
        description: 'Specify whether all visualizations will share the same scale in the trellis container.',
    },
    trellisSortBy: {
        default: 'result',
        description:
            'Specify the sort method for the individual visualizations in the trellis container. Use result to sort by search result order, or name to sort alphabetically by visualization label.',
        type: 'string',
        pattern: getPattern(['result', 'name']),
        enum: ['result', 'name'],
    },
    trellisSortOrder: {
        default: 'ascending',
        description:
            'Specify the sort order for the individual visualizations in the trellis container. This setting applies to the sort method specified in trellisSortBy.',
        type: 'string',
        pattern: getPattern(['ascending', 'descending']),
        enum: ['ascending', 'descending'],
    },
    trellisSplitBy: {
        description:
            'Specify the field name of the column with categories used, or “aggregations”, to split the data into individual visualizations for trellis display, if applicable. If a SPL `timechart` command is used, this may default to the SPL `by` clause field. If a SPL `chart` command is used, this may default to the first string field.',
        type: 'string',
    },
    xAxisLabelRotation: {
        description: 'Specify the rotation of the x-axis label in degrees.',
        enum: [-90, -45, 0, 45, 90],
        type: 'number',
        default: 0,
    },
    xAxisLabelVisibility: {
        default: 'auto',
        description: 'Specify whether to show labels on the x-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    yAxisLabelVisibility: {
        default: 'auto',
        description: 'Specify whether to show labels on the y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    y2AxisLabelVisibility: {
        default: 'auto',
        description: 'Specify whether to show labels on the second y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    xAxisLineVisibility: {
        default: 'hide',
        description: 'Specify whether to show the x-axis line.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    yAxisLineVisibility: {
        default: 'hide',
        description: 'Specify whether to show the y-axis line.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    y2AxisLineVisibility: {
        default: 'hide',
        description: 'Specify whether to show the second y-axis line.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    xAxisMajorTickSize: {
        default: 6,
        description: 'Specify the size (px) of major tick marks on the x-axis.',
        type: 'number',
    },
    yAxisMajorTickSize: {
        default: 6,
        description: 'Specify the size (px) of major tick marks on the y-axis.',
        type: 'number',
    },
    y2AxisMajorTickSize: {
        default: 6,
        description: 'Specify the size (px) of major tick marks on the second y-axis.',
        type: 'number',
    },
    yAxisMinorTickSize: {
        default: 6,
        description: 'Specify the size (px) of minor tick marks on the y-axis.',
        type: 'number',
    },
    y2AxisMinorTickSize: {
        default: 6,
        description: 'Specify the size (px) of minor tick marks on the second y-axis.',
        type: 'number',
    },
    xAxisMajorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show major tick marks on the x-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    yAxisMajorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show major tick marks on the y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    y2AxisMajorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show major tick marks on the second y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    yAxisMinorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show minor tick marks on the y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    y2AxisMinorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show minor tick marks on the second y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    xAxisMaxLabelParts: {
        default: 3,
        minimum: 1,
        maximum: 3,
        description:
            'Specify the maximum number of time-parts for a tick label. The 3 possible parts are year, month, and time. Values can range from 1 to 3.',
        type: 'number',
    },
    yAxisScale: {
        type: 'string',
        pattern: getPattern(['linear', 'log']),
        description: 'Specify the type of scale that applies to a numerical y-axis.',
        default: 'linear',
    },
    y2AxisScale: {
        type: 'string',
        pattern: getPattern(['linear', 'log']),
        description: 'Specify the type of scale that applies to a numerical second y-axis.',
        default: 'linear',
    },
    xAxisTitleText: {
        description: 'Specify the title of the x-axis.',
        type: 'string',
    },
    yAxisTitleText: {
        description: 'Specify the title of the y-axis.',
        type: 'string',
    },
    y2AxisTitleText: {
        description: 'Specify the title of the second y-axis.',
        type: 'string',
    },
    xAxisTitleVisibility: {
        default: 'show',
        description: 'Specify whether to show the title of the x-axis.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    yAxisTitleVisibility: {
        default: 'show',
        description: 'Specify whether to show the title of the y-axis.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    y2AxisTitleVisibility: {
        default: 'show',
        description: 'Specify whether to show the title of the second y-axis.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    yAxisAbbreviation: {
        default: 'auto',
        description:
            'Specify whether to abbreviate large y-axis values with the closest International System of Units (SI) prefix.',
        enum: ['auto', 'off'],
        pattern: getPattern(['off', 'auto']),
        type: 'string',
    },
    y2AxisAbbreviation: {
        default: 'auto',
        description:
            'Specify whether to abbreviate large second y-axis values with the closest International System of Units (SI) prefix.',
        enum: ['auto', 'off'],
        pattern: getPattern(['off', 'auto']),
        type: 'string',
    },
    yAxisMajorTickInterval: {
        default: 'auto',
        description: 'Specify the spacing unit between major tick marks along the numeric y-axis.',
        type: ['string', 'number'],
    },
    y2AxisMajorTickInterval: {
        default: 'auto',
        description: 'Specify the spacing unit between major tick marks along the numeric second y-axis.',
        type: ['string', 'number'],
    },
    yAxisMax: {
        type: ['string', 'number'],
        description: 'Specify the largest value for the visible y-axis range.',
        default: 'auto',
    },
    y2AxisMax: {
        type: ['string', 'number'],
        description: 'Specify the largest value for the visible second y-axis range.',
        default: 'auto',
    },
    yAxisMin: {
        type: ['string', 'number'],
        description: 'Specify the smallest value for the visible y-axis range.',
        default: 'auto',
    },
    y2AxisMin: {
        type: ['string', 'number'],
        description: 'Specify the smallest value for the visible second y-axis range.',
        default: 'auto',
    },
    overlayFields: {
        description:
            'Specify one or more fields to differentiate on the chart and display as chart overlays.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    showOverlayY2Axis: {
        default: false,
        description:
            'Enable a second y-axis for chart overlays. All overlay fields will map to a second y-axis.',
        type: 'boolean',
    },
    showRoundedY2AxisLabels: {
        default: true,
        description: 'Specify whether to round second y-axis values to the nearest integer.',
        type: 'boolean',
    },
    y2Fields: {
        default: '> y2 | getField()',
        description: 'Specify one or more fields to map to the second y-axis.',
        type: ['array', 'string'],
        items: { type: 'string' },
    },
});
